import { Show, untrack, type VoidComponent } from "solid-js";
import type { User } from "../../../state/user";
import { useTranslation } from "../../../state/i18n";
import InputModal from "../../../components/modal/InputModal";
import { literal, object, pipe } from "valibot";
import { TextInput } from "../../../components/inputs/TextInput";
import { errorTranslation, requireText } from "../../../utils/validation/base";
import { keyFromSelector } from "i18next";
import { MAX_CHARACTERS_MD } from "../../../constants";
import { Translation } from "../../../utils/Translation";
import { resolveFullName } from "../../../utils/userUtil";
import UserService from "../../../services/UserService";
import { useCustomerState } from "../../../state/customer";
import GenericFailureModal from "../../GenericFailureModal";
import { useModalManager } from "../../../components/modal/ModalManager";
import type { MoreAppError } from "../../../MoreAppError";
import type { Invite } from "../../../state/invite.ts";
import UserId from "../../../state/UserId.ts";

type DeleteUserModalProps = {
  user: User | Invite;
  onSuccess?: () => void;
};

const deleteUserSchema = (emailAddress: string) =>
  object({
    emailAddress: pipe(
      requireText(MAX_CHARACTERS_MD),
      literal(
        emailAddress,
        errorTranslation(keyFromSelector(($) => $.DELETE_USER_MODAL_INPUT_NAME_VALIDATION_NOT_EQUAL)),
      ),
    ),
  });

type ResolvedState = { isInvited: true; user: Invite } | { isInvited: false; user: User };

const DeleteUserModal: VoidComponent<DeleteUserModalProps> = (props) => {
  const { t } = useTranslation();

  const resolvedState = (): ResolvedState =>
    props.user.id instanceof UserId
      ? { isInvited: false, user: props.user as User }
      : { isInvited: true, user: props.user as Invite };
  const customer = useCustomerState();

  const revokeInvite = UserService.revokeInvitation(customer.customerId);
  const deleteUser = UserService.removeUser(customer.customerId);
  const { replace } = useModalManager();

  return (
    <Show when={resolvedState()}>
      {(resolvedState) => {
        const resolved = resolvedState();

        return (
          <InputModal
            variant="destructive"
            title={t(($) => (resolved.isInvited ? $.DELETE_USER_MODAL_REVOKE_TITLE : $.DELETE_USER_MODAL_TITLE))}
            confirmLabel={t(($) =>
              resolved.isInvited ? $.DELETE_USER_MODAL_ACTIONS_REVOKE : $.DELETE_USER_MODAL_ACTIONS_DELETE,
            )}
            cancelLabel={t(($) => $.CANCEL_BUTTON)}
            onConfirm={() =>
              new Promise((resolve, reject) => {
                const mutateOptions = {
                  onSuccess: () => {
                    resolve();
                    props.onSuccess?.();
                  },
                  onError: (err: MoreAppError) => {
                    replace({
                      content: () => (
                        <GenericFailureModal title={t(($) => $.DELETE_USER_FAILURE_MODAL_TITLE)} error={err} />
                      ),
                    });
                    reject(err);
                  },
                };

                if (resolved.isInvited) {
                  revokeInvite.mutate(
                    untrack(() => resolved.user.id),
                    mutateOptions,
                  );
                } else {
                  deleteUser.mutate(
                    untrack(() => resolved.user.id),
                    mutateOptions,
                  );
                }
              })
            }
            initialValue={{ emailAddress: undefined }}
            schema={deleteUserSchema(props.user.emailAddress)}
          >
            {({ value, updateValue, error }) => (
              <div class="flex flex-col gap-6">
                <div class="text-base-regular wrap-break-word text-secondary">
                  <Translation
                    key={keyFromSelector(($) =>
                      resolved.isInvited ? $.DELETE_USER_MODAL_DESCRIPTION_INVITE : $.DELETE_USER_MODAL_DESCRIPTION,
                    )}
                    options={{
                      emailAddress: props.user.emailAddress,
                      fullName: !resolved.isInvited && resolveFullName(resolved.user),
                    }}
                    components={{
                      b: (props) => <span class="text-base-semibold text-primary">{props.children}</span>,
                    }}
                  />
                </div>

                <TextInput
                  label={t(($) => $.DELETE_USER_MODAL_EMAIL_INPUT_TITLE)}
                  value={value("emailAddress")}
                  required
                  onBlur={(e) => {
                    updateValue("emailAddress", e.target.value);
                  }}
                  onInput={(e) => {
                    updateValue("emailAddress", e.target.value);
                  }}
                  errorMessage={error("emailAddress")}
                />
              </div>
            )}
          </InputModal>
        );
      }}
    </Show>
  );
};

export default DeleteUserModal;
